import { useAuth } from '../auth/useAuth'
import { useRef, useEffect, useState } from 'react'
import { usePreview } from './documentHooks'
import {
	CurrentDocumentData,
	GetPreviewDataResponse,
} from '../../lib/api/types/document.interfaces'
import toastCaller, { errors } from '../../lib/utils/toastCaller'

export interface PreviewPollingResult {
	status: string
	data: GetPreviewDataResponse | null
	error: string | null
	isLoading: boolean
	retry: () => void
}

interface UsePreviewWithPollingProps {
	id: string
	versionId?: string
	enabled?: boolean
	pollInterval?: number
	maxRetries?: number
	fileSize?: number
	file?: CurrentDocumentData
}

const NOT_READY_ERRORS = [
	'PREVIEW_NOT_READY',
	'THUMBNAIL_NOT_READY',
	'FILE_PREVIEW_NOT_READY',
	'FILE_THUMBNAIL_NOT_READY',
] as const

// Errors that need special interval based on file size
const FILE_SIZE_DEPENDENT_ERRORS = [
	'FILE_PREVIEW_NOT_READY',
	'FILE_THUMBNAIL_NOT_READY',
] as const

export const PREVIEW_LOADING_STATUSES = [
	'PREVIEW_NOT_READY',
	'THUMBNAIL_NOT_READY',
	'FILE_PREVIEW_NOT_READY',
	'FILE_THUMBNAIL_NOT_READY',
] as const

export const usePreviewWithPolling = ({
	id,
	versionId,
	enabled = true,
	pollInterval = 15000,
	maxRetries = 15,
	fileSize,
	file,
}: UsePreviewWithPollingProps): PreviewPollingResult => {
	const { isAuthenticated } = useAuth()
	// Request the exact current version so a pending conversion cannot return an older preview.
	const previewVersionId =
		versionId ||
		file?.versions?.find((version) => version.currentVersion)?.id ||
		file?.versionId ||
		undefined
	const lastLoadedRef = useRef<{ id: string; revision: string } | null>(null)
	// Track polling attempts separately since failureCount doesn't increase with refetchInterval
	const pollingAttemptsRef = useRef(0)
	const [hasTimedOut, setHasTimedOut] = useState(false)

	useEffect(() => {
		pollingAttemptsRef.current = 0
		setHasTimedOut(false)
	}, [id, previewVersionId])

	const {
		data,
		error,
		errorUpdatedAt,
		isLoading,
		refetch,
		status: queryStatus,
	} = usePreview(
		{ id, versionId: previewVersionId },
		{
			enabled:
				enabled && isAuthenticated && Boolean(id) && Boolean(file || versionId),
			// Use cached preview when reopening the same file — avoids refetch, new signed URL and PDF reload
			staleTime: 1000 * 60 * 5, // 5 min
			refetchInterval: (query) => {
				// If we have data, stop polling
				if (query.state.data) {
					return false
				}

				const errorCode = (query.state.error as any)?.response?.data
					?.errors?.[0]

				// Only poll for specific errors with file size dependent interval
				if (FILE_SIZE_DEPENDENT_ERRORS.includes(errorCode as any)) {
					// Check retry count
					if (pollingAttemptsRef.current >= maxRetries) {
						return false
					}

					// Calculate interval based on file size
					const oneMB = 1 * 1024 * 1024 // 1MB in bytes
					const interval = fileSize && fileSize < oneMB ? 10000 : pollInterval

					return interval
				}

				// For other polling errors, use default interval
				if (NOT_READY_ERRORS.includes(errorCode as any)) {
					if (pollingAttemptsRef.current >= maxRetries) {
						return false
					}
					return pollInterval
				}

				// Stop polling for other errors or when no error
				return false
			},
			refetchIntervalInBackground: false,
			// Don't retry automatically - we use refetchInterval for polling.
			retry: false,
		},
	)

	useEffect(() => {
		if (!enabled || versionId) {
			lastLoadedRef.current = null
			return
		}
		if (!file || !data?.data?.downloadUrl || queryStatus !== 'success') return
		const revision = previewVersionId ?? String(file.version)
		const previous = lastLoadedRef.current
		lastLoadedRef.current = { id, revision }
		if (previous?.id === id && previous.revision !== revision) {
			toastCaller({
				type: 'info',
				message: 'The latest file version has been loaded.',
				toastId: `preview-updated-${id}-${revision}`,
			})
		}
	}, [enabled, versionId, file, id, previewVersionId, data, queryStatus])

	// Track polling attempts and reset counter when error/data changes
	useEffect(() => {
		const errorCode = (error as any)?.response?.data?.errors?.[0]

		// A SignalR query reset clears errorUpdatedAt; a normal refetch keeps it.
		if (data || (!error && errorUpdatedAt === 0)) {
			pollingAttemptsRef.current = 0
			setHasTimedOut(false)
			return
		}

		// Increment counter when we have a polling error (after each refetch)
		if (error && NOT_READY_ERRORS.includes(errorCode as any)) {
			pollingAttemptsRef.current += 1
			setHasTimedOut(pollingAttemptsRef.current >= maxRetries)
		}
	}, [error, errorUpdatedAt, data, id, previewVersionId, maxRetries])

	const getStatusAndMessage = () => {
		if (isLoading && queryStatus === 'pending') {
			return { status: 'loading', message: null }
		}

		if (data) {
			return { status: 'ready', message: null }
		}

		if (hasTimedOut) {
			return {
				status: 'PREVIEW_GENERATION_TIMEOUT',
				message: errors.PREVIEW_GENERATION_TIMEOUT(),
			}
		}

		if (error) {
			const errorCode = (error as any)?.response?.data?.errors?.[0]
			const statusCode = (error as any)?.response?.status
			if (statusCode === 400 && errorCode) {
				const message = errors[errorCode]?.() || 'Failed to load preview'
				return { status: errorCode, message }
			}

			return {
				status: 'error',
				message:
					(error as any)?.response?.data?.message || 'Failed to load preview',
			}
		}

		return { status: 'idle', message: null }
	}

	const { status, message: errorMessage } = getStatusAndMessage()

	return {
		status,
		data: data || null,
		error: errorMessage,
		isLoading: isLoading || PREVIEW_LOADING_STATUSES.includes(status as any),
		retry: () => {
			pollingAttemptsRef.current = 0
			setHasTimedOut(false)
			void refetch()
		},
	}
}
